'use client';

import { useMemo, useEffect, useState } from 'react';
import { Home, MapPin, Phone, Landmark, Mail } from 'lucide-react';
import { Typography } from '@/components/ui/typography';
import { useTranslations, useLocale } from 'next-intl';

type ContactAddress = {
    id: string;
    country: string;
    address: string;
    phones: string[];
    email: string;
    icon?: typeof Home;
};

interface ContactAddressSectionProps {
    className?: string;
    /** SSG: pre-fetched contact addresses (skip client fetch when provided) */
    initialAddresses?: { id: string; country: string; address: string; phones: string[]; email: string }[];
}

export function ContactAddressSection({ className = '', initialAddresses }: ContactAddressSectionProps) {
    const t = useTranslations('home.contact');
    const locale = useLocale();
    const addressIcons = [Home, Landmark, Landmark];

    const [addresses, setAddresses] = useState<ContactAddress[]>(() => {
        if (initialAddresses?.length) {
            return initialAddresses.map((item, idx) => ({
                id: item.id,
                country: item.country,
                address: item.address,
                phones: Array.isArray(item.phones) ? item.phones : [],
                email: item.email ?? '',
                icon: addressIcons[idx] ?? Home,
            }));
        }
        return [];
    });
    const [isLoading, setIsLoading] = useState(!initialAddresses?.length);

    useEffect(() => {
        if (initialAddresses?.length) {
            setAddresses(
                initialAddresses.map((item, idx) => ({
                    id: item.id,
                    country: item.country,
                    address: item.address,
                    phones: Array.isArray(item.phones) ? item.phones : [],
                    email: item.email ?? '',
                    icon: addressIcons[idx] ?? Home,
                }))
            );
            setIsLoading(false);
            return;
        }
        const fetchAddresses = async () => {
            try {
                setIsLoading(true);
                const res = await fetch(`/api/contact-addresses?locale=${encodeURIComponent(locale)}`, {
                    cache: 'no-store',
                });
                if (!res.ok) {
                    // Fallback to translations if API fails
                    const rawAddresses = t.raw('addresses');
                    const baseAddresses = Array.isArray(rawAddresses) ? (rawAddresses as any[]) : [];
                    setAddresses(
                        baseAddresses.map((address, idx) => ({
                            id: `fallback-${idx}`,
                            country: address.country || '',
                            address: address.address || '',
                            phones: Array.isArray(address.phones) ? address.phones : address.phones ? [address.phones] : [],
                            email: '',
                            icon: addressIcons[idx] ?? Home,
                        }))
                    );
                    return;
                }
                const data = await res.json();
                const items: ContactAddress[] = Array.isArray(data?.items)
                    ? data.items.map((item: any, idx: number) => {
                        // Ensure phones is properly parsed as an array
                        let phonesArray: string[] = [];
                        if (Array.isArray(item.phones)) {
                            phonesArray = item.phones;
                        } else if (item.phones) {
                            try {
                                const parsed = typeof item.phones === 'string' ? JSON.parse(item.phones) : item.phones;
                                phonesArray = Array.isArray(parsed) ? parsed : [String(parsed)];
                            } catch {
                                phonesArray = [String(item.phones)];
                            }
                        }

                        return {
                            id: String(item.id),
                            country: String(item.country || ''),
                            address: String(item.address || ''),
                            phones: phonesArray,
                            email: String(item.email || ''),
                            icon: addressIcons[idx] ?? Home,
                        };
                    })
                    : [];
                setAddresses(items);
            } catch (error) {

                // Fallback to translations on error (table might not exist yet)
                const rawAddresses = t.raw('addresses');
                const baseAddresses = Array.isArray(rawAddresses) ? (rawAddresses as any[]) : [];
                setAddresses(
                    baseAddresses.map((address, idx) => ({
                        id: `fallback-${idx}`,
                        country: address.country || '',
                        address: address.address || '',
                        phones: Array.isArray(address.phones) ? address.phones : address.phones ? [address.phones] : [],
                        email: '',
                        icon: addressIcons[idx] ?? Home,
                    }))
                );
                setIsLoading(false);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAddresses();
    }, [locale, initialAddresses?.length]);

    if (isLoading) {
        return (
            <div className={`space-y-8 ${className}`}>
                {[1, 2].map((i) => (
                    <div
                        key={i}
                        className="h-48 bg-gray-200 dark:bg-gray-700 rounded-2xl animate-pulse"
                    />
                ))}
            </div>
        );
    }

    return (
        <div className={`space-y-4 ${className}`}>
            {addresses.map((addressInfo, index) => (
                <div
                    key={addressInfo.id || index}
                    className="rounded-2xl p-6 md:p-8 border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-[0_14px_40px_rgba(15,23,42,0.08)] dark:shadow-[0_18px_50px_rgba(0,0,0,0.45)]"
                >
                    <div className="flex items-center gap-3 mb-4">
                        {addressInfo.icon && (
                            <addressInfo.icon className="w-6 h-6 text-primary-500" />
                        )}
                        <Typography variant="h4" className="text-gray-900 dark:text-white">
                            {addressInfo.country}
                        </Typography>
                    </div>
                    <div className="flex items-start gap-3 mb-4">
                        <MapPin className="w-5 h-5 text-gray-500 dark:text-gray-500 mt-0.5 flex-shrink-0" />
                        <Typography variant="p" className="text-gray-700 dark:text-gray-400 text-sm leading-relaxed">
                            {addressInfo.address}
                        </Typography>
                    </div>
                    {addressInfo.email && (
                        <div className="flex items-center gap-2 mb-2">
                            <Mail className="w-5 h-5 text-gray-500 dark:text-gray-500 flex-shrink-0" />
                            <a
                                href={`mailto:${addressInfo.email}`}
                                className="text-gray-700 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-300 transition-colors text-sm"
                            >
                                {addressInfo.email}
                            </a>
                        </div>
                    )}
                    {(addressInfo.phones ?? []).length > 0 && (
                        <div className="flex flex-wrap gap-4">
                            {(addressInfo.phones ?? []).map((phone, phoneIndex) => (
                                <div key={phoneIndex} className="flex items-center gap-2">
                                    <Phone className="w-5 h-5 text-gray-500 dark:text-gray-500" />
                                    <a
                                        href={`tel:${phone.replace(/\s/g, '')}`}
                                        className="text-gray-700 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-300 transition-colors text-sm"
                                    >
                                        {phone}
                                    </a>
                                </div>
                            ))}
                        </div>
                    )}

                </div>
            ))}
        </div>
    );
}

